import type { CatalogImageType, CatalogProductImage } from "@shared/types";
import { ALT_TEXT_MAX_LENGTH, CATALOG_IMAGE_TYPES } from "@shared/types";
import { Button } from "@shared-ui/button";
import type { JSX } from "react";
import { useRef } from "react";
import { useImageUpload } from "@/components/ui/image-uploader";
import { catalogImageSrc } from "@/features/catalog/lib/images";
import { uploadImage } from "@/features/catalog/services/catalog-api.service";

/**
 * A product's images.
 *
 * WHAT THIS STORES. `catalogProducts.images` holds `{url, type, alt}` per
 * image. It was a bare `jsonb string[]` until the catalogue image metadata
 * plan's U1 promoted it, and this file used to explain at length why alt text
 * was impossible -- that constraint is gone and so is the explanation.
 *
 * PRIMARY IS STILL POSITIONAL. The first image is the primary one, and that did
 * NOT change with the promotion: no `isPrimary` flag was added, because nothing
 * asked for one. The reorder buttons and Make primary are untouched by this
 * unit, which is the point -- promoting the column added fields and changed no
 * existing control.
 *
 * ALT AND TYPE ARE COLLAPSED behind a per-image disclosure. The manager renders
 * every image inline, and at twenty images a text input and a select per row is
 * a wall nobody can work through.
 *
 * UN-DESCRIBED IMAGES ARE MARKED, and counted at the top. The job here is
 * finding the images nobody has described; a per-image field that looks
 * identical whether it is filled or empty does not surface that, and a field
 * that ships and stays empty is the likeliest outcome of adding one.
 *
 * The publish gate requires at least one image, so this is what makes plan
 * 008's M1 reachable: without it a product could be created and completed but
 * never published. It does NOT require alt text -- that is Q1, deferred, and R8
 * leaves the gate as it was.
 */

/**
 * The types the SERVER will accept, not the ones a browser can open.
 *
 * `ALLOWED_FILE_TYPES` is the source of truth -- `apps/backend/.env` sets
 * `image/jpeg,image/png,image/webp`, and multer's `fileFilter` rejects anything
 * else with "Invalid file type". An earlier version of this list also offered
 * AVIF, so the file picker let an operator choose a file the API would refuse:
 * the failure was reported honestly, but only after the upload, and only for a
 * format the screen had suggested.
 *
 * This list cannot read that env var, so it will drift if the server's is
 * widened. Narrower than the server is a missed convenience; wider is a promise
 * the API breaks, which is why it stays conservative.
 */
const ACCEPT = "image/jpeg,image/png,image/webp";

/** What each stored type value is called on screen. */
const IMAGE_TYPE_LABELS: Record<CatalogImageType, string> = {
	front: "Front",
	detail: "Detail",
	in_situ: "In situ",
	spec_sheet: "Spec sheet",
};

/** Multer refuses above this, and so does the catalogue image route. */
/*
 * NO LOCAL SIZE CONSTANT ANY MORE. The check lives in `useImageUpload`, so
 * nothing here needs the number.
 *
 * The helper text below still says "10MB" as a literal, and so does the hook's
 * rejection message. That duplication predates this change and is not fixed by
 * it -- saying so rather than leaving a comment claiming the two are linked,
 * which an earlier version of this file did.
 */

export function ProductMedia({
	productId,
	images,
	onChange,
	onUploadingChange,
	disabled,
	invalid,
}: {
	/** Storage is keyed by product, so the upload needs to know which one. */
	productId: string;
	images: CatalogProductImage[];
	onChange: (images: CatalogProductImage[]) => void;
	/**
	 * Raised while files are in flight, so the page can stop a save landing
	 * between an upload starting and its URL arriving.
	 */
	onUploadingChange?: (uploading: boolean) => void;
	disabled?: boolean;
	/** The publish gate named images as missing. */
	invalid?: boolean;
}): JSX.Element {
	/*
	 * The LATEST list, not the one captured when the upload began.
	 *
	 * `upload()` awaits one request per file and then appends. Reading the
	 * `images` prop at that point reads the value from the render that started
	 * the upload -- so an image removed while files were uploading came straight
	 * back, and the operator's deletion was undone by an action they took before
	 * it.
	 *
	 * THE HOOK DOES NOT CARRY THIS. `useImageUpload` returns the URLs that made
	 * it and knows nothing about the list they are appended to, which is right --
	 * but it means adopting the hook cannot mean deleting this ref.
	 */
	const latestImages = useRef(images);
	latestImages.current = images;

	/*
	 * The three upload policies now come from the shared hook: one file at a
	 * time so a rejected file does not lose the rest, a local size check that
	 * names the file rather than spending the upload, and the input cleared
	 * afterwards so the same file picked twice still fires.
	 *
	 * This screen implemented all three itself, identically, which is exactly the
	 * duplication KTD3 is about -- two copies of an upload policy drift, and the
	 * one nobody is looking at is the one that drifts.
	 */
	const { state, inputRef, run } = useImageUpload((file) =>
		uploadImage(productId, file),
	);
	const { uploading, rejected: failed, addedAny } = state;

	/** Replace one image, leaving its position and every other image alone. */
	function patch(index: number, fields: Partial<CatalogProductImage>): void {
		onChange(
			images.map((image, at) =>
				at === index ? { ...image, ...fields } : image,
			),
		);
	}

	/** How many images nobody has described yet. Null and "" both count. */
	const undescribed = images.filter((image) => !image.alt).length;

	function move(from: number, to: number): void {
		/*
		 * DELIBERATE REDUNDANCY, and it cannot be shown to work on its own: the
		 * up/down buttons are disabled at the ends, so nothing reaches this guard
		 * while that holds, and mutating it away leaves every test green. It is
		 * kept because `move(index, 0)` from Make primary and the two step calls
		 * are three callers of one function, and the day a fourth arrives without
		 * its own bounds check, this is what stops a splice at index -1 silently
		 * moving an image to the end of the list.
		 */
		if (to < 0 || to >= images.length) return;
		const next = [...images];
		const [moved] = next.splice(from, 1);
		next.splice(to, 0, moved);
		onChange(next);
	}

	async function upload(files: FileList | null): Promise<void> {
		if (!files || files.length === 0) return;
		/*
		 * Its own guard, not just the Button's `disabled`. A function that is only
		 * safe because of how it happens to be reached is safe until someone
		 * reaches it another way.
		 *
		 * DELIBERATE REDUNDANCY, and it cannot be shown to work -- the same
		 * admission `move` above carries. Both the button and the input are
		 * disabled while `uploading > 0`, so nothing reaches this line while that
		 * holds, and deleting it leaves every test green. Kept for the day a
		 * second caller arrives without that disabling, which is exactly how the
		 * `move` guard earned its place.
		 */
		if (uploading > 0) return;

		/*
		 * `onUploadingChange` IS NOT THE HOOK'S. `product-editor` reads it to
		 * block Save while files are in flight, and U9's unsaved-work guard reads
		 * it too -- so it has to bracket the whole run, including the awaits the
		 * hook owns. The hook exposes `state.uploading` for rendering and has no
		 * callback; wrapping is the adoption, not a workaround for it.
		 */
		onUploadingChange?.(true);
		const uploaded = await run(files);
		if (uploaded.length > 0) {
			/*
			 * A NEW IMAGE ARRIVES UNDESCRIBED, not with a placeholder. `null` is
			 * what the count below looks for, and it is the same value the
			 * migration wrote: an upload nobody has described yet is in exactly
			 * the state a backfilled row is in, and should read that way.
			 */
			onChange([
				...latestImages.current,
				...uploaded.map((url) => ({ url, type: null, alt: null })),
			]);
		}
		onUploadingChange?.(false);
	}

	return (
		<div className="grid gap-4">
			<div className="flex flex-wrap items-center gap-3">
				<input
					ref={inputRef}
					type="file"
					accept={ACCEPT}
					multiple
					className="hidden"
					disabled={disabled || uploading > 0}
					onChange={(event) => void upload(event.target.files)}
					data-testid="product-image-input"
				/>
				<Button
					type="button"
					variant="outline"
					disabled={disabled || uploading > 0}
					onClick={() => inputRef.current?.click()}
				>
					{uploading > 0 ? `Uploading ${uploading}…` : "Add images"}
				</Button>
				<span className="text-xs text-muted-foreground">
					JPEG, PNG or WebP, up to 10MB each.
				</span>
			</div>

			{failed.length > 0 ? (
				/*
				 * Named, not counted -- "2 files failed" leaves the operator comparing
				 * what they picked against what appeared.
				 *
				 * And "the rest were added" is only said when there IS a rest. When
				 * every file failed, that sentence was a claim about an empty set: the
				 * operator read that something had been added, looked at an unchanged
				 * list, and had to work out which of the two was lying.
				 */
				<p className="text-sm text-red-700">
					Could not upload: {failed.join(", ")}.
					{addedAny ? " The rest were added." : null}
				</p>
			) : null}

			{undescribed > 0 ? (
				/*
				 * THE COUNT, not just a per-image marker. Finding the images nobody
				 * has described is the job on this screen, and a marker only answers
				 * it for the rows currently on screen. A number answers "is there
				 * anything left to do here" without scrolling.
				 *
				 * `status`, not `alert`: it is a standing fact about the product, not
				 * something that just went wrong.
				 */
				<p role="status" className="text-xs text-amber-800">
					{`${undescribed} of ${images.length} image${
						images.length === 1 ? "" : "s"
					} still ${undescribed === 1 ? "has" : "have"} no description.`}
				</p>
			) : null}

			{images.length === 0 ? (
				<div
					className={`rounded-xl border border-dashed p-8 text-center text-sm ${
						invalid ? "border-red-300 text-red-700" : "text-muted-foreground"
					}`}
				>
					<p>No images yet.</p>
					<p className="mt-1">
						A product cannot be published without at least one, and one with no
						image is effectively invisible in browse.
					</p>
				</div>
			) : (
				<ul className="grid gap-2">
					{images.map((image, index) => (
						<li
							/*
							 * Keyed on the URL, not on the position.
							 *
							 * Position is what the reorder buttons CHANGE, so an index key would
							 * hand React a different identity for the same image every time it
							 * moved -- remounting the row, reloading the image, and throwing away
							 * any open disclosure and half-typed alt text with it. The URL is a
							 * genuine identity: two entries sharing one would be the same picture
							 * listed twice, which nothing in the product intends.
							 */
							key={image.url}
							className="rounded-xl border p-2"
						>
							<div className="flex items-center gap-3">
								<img
									src={catalogImageSrc(image.url)}
									alt=""
									className="h-16 w-16 shrink-0 rounded-lg object-cover"
								/>
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-center gap-1.5">
										{index === 0 ? (
											<span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800">
												Primary
											</span>
										) : null}
										{image.type ? (
											<span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
												{IMAGE_TYPE_LABELS[image.type]}
											</span>
										) : null}
										{/*
										 * The marker the count at the top is counting. Without it a
										 * described and an undescribed image look identical until the
										 * operator opens each one in turn.
										 */}
										{image.alt ? null : (
											<span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800">
												Needs description
											</span>
										)}
									</div>
									<p className="truncate text-xs text-muted-foreground">
										{image.url}
									</p>
								</div>
								<div className="flex shrink-0 gap-1">
									{/*
									 * Buttons rather than drag-and-drop. The plan asks for
									 * drag-and-drop and this is not it -- but a drag handle is
									 * unusable from a keyboard without a parallel control anyway,
									 * so this is the half that has to exist either way. Dragging
									 * can be added over it.
									 */}
									<Button
										type="button"
										size="sm"
										variant="ghost"
										disabled={disabled || uploading > 0 || index === 0}
										aria-label={`Move image ${index + 1} up`}
										onClick={() => move(index, index - 1)}
									>
										↑
									</Button>
									<Button
										type="button"
										size="sm"
										variant="ghost"
										disabled={
											disabled || uploading > 0 || index === images.length - 1
										}
										aria-label={`Move image ${index + 1} down`}
										onClick={() => move(index, index + 1)}
									>
										↓
									</Button>
									<Button
										type="button"
										size="sm"
										variant="ghost"
										disabled={disabled || uploading > 0 || index === 0}
										aria-label={`Make image ${index + 1} primary`}
										onClick={() => move(index, 0)}
									>
										Make primary
									</Button>
									<Button
										type="button"
										size="sm"
										variant="ghost"
										disabled={disabled || uploading > 0}
										aria-label={`Remove image ${index + 1}`}
										onClick={() =>
											onChange(images.filter((_, at) => at !== index))
										}
									>
										Remove
									</Button>
								</div>
							</div>

							<details className="mt-2">
								<summary className="cursor-pointer text-xs text-muted-foreground">
									Describe image {index + 1}
								</summary>
								<div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
									<label className="grid gap-1 text-xs">
										{/*
										 * WHAT THE IMAGE SHOWS, not what the product is called. The
										 * product name is already announced; repeating it here is the
										 * behaviour this field exists to replace.
										 */}
										<span>Alt text — describe what this image shows</span>
										<input
											type="text"
											value={image.alt ?? ""}
											maxLength={ALT_TEXT_MAX_LENGTH}
											disabled={disabled}
											className="rounded-lg border px-2 py-1"
											onChange={(event) =>
												/*
												 * Cleared back to NULL, not to "". Null is what the
												 * count and the marker read as undescribed, and an
												 * empty string would be a description nobody wrote.
												 */
												patch(index, { alt: event.target.value || null })
											}
										/>
										<span className="text-muted-foreground">
											{(image.alt ?? "").length}/{ALT_TEXT_MAX_LENGTH}
										</span>
									</label>
									<label className="grid gap-1 text-xs">
										<span>Type</span>
										<select
											value={image.type ?? ""}
											disabled={disabled}
											className="rounded-lg border px-2 py-1"
											onChange={(event) =>
												patch(index, {
													type: (event.target.value ||
														null) as CatalogImageType | null,
												})
											}
										>
											<option value="">Not set</option>
											{CATALOG_IMAGE_TYPES.map((value) => (
												<option key={value} value={value}>
													{IMAGE_TYPE_LABELS[value]}
												</option>
											))}
										</select>
									</label>
								</div>
							</details>
						</li>
					))}
				</ul>
			)}

			<p className="text-xs text-muted-foreground">
				The first image is the primary one — reorder to change which. Alt text
				describes what an image shows, for anyone who cannot see it; it is not
				required to publish.
			</p>
		</div>
	);
}
